import { useState } from "react";
import { CaretRight, Lock, Plugs } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { MODULE_CATALOG } from "@/modules/apps/moduleCatalog";
import { ModuleAutomationSwitches } from "@/modules/apps/ModuleAutomationSwitches";
import { ModuleFeatureSwitches } from "@/modules/apps/ModuleFeatureSwitches";
import {
  enabledDependents,
  isOn,
  missingDependencies,
} from "@/modules/apps/moduleDependencies";
import {
  INTEGRATION_LABELS,
  MODULE_CATEGORIES,
  MODULE_LIST,
  MODULES,
  type ModuleId,
  type NomiModule,
} from "@/modules/registry";

export type ModuleRow = {
  module_id: ModuleId;
  enabled: boolean;
  options: Record<string, unknown>;
};

export type ModuleChange = {
  moduleId: ModuleId;
  enabled: boolean;
  options?: Record<string, unknown>;
};

type ManagerProps = {
  rows: ModuleRow[];
  busyModuleId?: ModuleId | null;
  onChange: (changes: ModuleChange[]) => void;
};

/**
 * One company's modules, grouped by category (platform console). A card
 * opens the module: switch it on/off, its options and its features.
 * Turning a module on also turns on what it depends on; a module others
 * need cannot be turned off.
 */
export const ModulesManager = ({
  rows,
  busyModuleId,
  onChange,
}: ManagerProps) => {
  const [openId, setOpenId] = useState<ModuleId | null>(null);

  return (
    <div className="flex flex-col gap-6">
      {MODULE_CATEGORIES.map((category) => {
        const modules = MODULE_LIST.filter(
          (module) => module.category === category.id,
        );
        if (!modules.length) return null;
        const onCount = modules.filter((m) => isOn(rows, m.id)).length;
        return (
          <section key={category.id} className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-muted-foreground">
              {category.label}{" "}
              <span className="font-normal">
                · {onCount}/{modules.length} on
              </span>
            </h3>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {modules.map((module) => (
                <ModuleCard
                  key={module.id}
                  module={module}
                  rows={rows}
                  onOpen={() => setOpenId(module.id)}
                />
              ))}
            </div>
          </section>
        );
      })}

      <Sheet
        open={openId != null}
        onOpenChange={(open) => {
          if (!open) setOpenId(null);
        }}
      >
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          {openId ? (
            <ModuleDetail
              module={MODULES[openId]}
              rows={rows}
              busy={busyModuleId != null}
              onChange={onChange}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const featureCount = (module: NomiModule, rows: ModuleRow[]) => {
  const features = module.features ?? [];
  const row = rows.find((r) => r.module_id === module.id);
  const on = features.filter(
    (feature) =>
      row?.options?.[feature.key] === true &&
      (feature.requires ?? []).every((id) => isOn(rows, id)),
  ).length;
  return { on, total: features.length };
};

const ModuleCard = ({
  module,
  rows,
  onOpen,
}: {
  module: NomiModule;
  rows: ModuleRow[];
  onOpen: () => void;
}) => {
  const on = isOn(rows, module.id);
  const features = featureCount(module, rows);
  const Icon = module.icon;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "flex items-center gap-3 rounded-lg border bg-card p-3 text-left transition-colors hover:border-primary/50 hover:bg-primary/5",
        on ? "border-primary/40" : "opacity-80",
      )}
    >
      <span
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-md",
          on ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
        )}
      >
        <Icon className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate font-medium">{module.name}</span>
          {module.core ? (
            <Badge variant="secondary" className="gap-1">
              <Lock className="size-3" />
              Included
            </Badge>
          ) : on ? (
            <Badge variant="success">On</Badge>
          ) : (
            <Badge variant="muted">Off</Badge>
          )}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {features.total
            ? `${features.on}/${features.total} features on`
            : module.description}
        </span>
      </span>
      <CaretRight className="size-4 shrink-0 text-muted-foreground" />
    </button>
  );
};

const ModuleDetail = ({
  module,
  rows,
  busy,
  onChange,
}: {
  module: NomiModule;
  rows: ModuleRow[];
  busy: boolean;
  onChange: (changes: ModuleChange[]) => void;
}) => {
  const on = isOn(rows, module.id);
  const row = rows.find((r) => r.module_id === module.id);
  const dependents = enabledDependents(rows, module.id);
  const missing = missingDependencies(rows, module.id);
  const lockedOn = module.core === true || (on && dependents.length > 0);
  const content = MODULE_CATALOG[module.id];
  const Icon = module.icon;

  const toggle = (next: boolean) => {
    if (next) {
      onChange([
        ...missing.map((id) => ({ moduleId: id, enabled: true })),
        { moduleId: module.id, enabled: true },
      ]);
    } else {
      onChange([{ moduleId: module.id, enabled: false }]);
    }
  };

  return (
    <div className="flex flex-col gap-5 px-4 pb-6">
      <SheetHeader className="px-0">
        <span
          className={cn(
            "mb-1 flex size-11 items-center justify-center rounded-xl",
            on ? "bg-primary/10 text-primary" : "bg-muted text-foreground",
          )}
        >
          <Icon className="size-6" />
        </span>
        <SheetTitle>{module.name}</SheetTitle>
        <SheetDescription>{content.tagline}</SheetDescription>
      </SheetHeader>

      <label className="flex items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3">
        <span className="min-w-0">
          <span className="block text-sm font-medium">
            {module.core ? "Included in every workspace" : "App is on"}
          </span>
          <span className="block text-xs text-muted-foreground">
            {lockedOn && !module.core
              ? `Needed by ${dependents.map((m) => m.name).join(", ")}`
              : !on && missing.length
                ? `Also turns on ${missing.map((id) => MODULES[id].name).join(", ")}`
                : module.description}
          </span>
        </span>
        <Switch
          checked={on}
          disabled={busy || lockedOn}
          onCheckedChange={toggle}
          aria-label={`${on ? "Turn off" : "Turn on"} ${module.name}`}
        />
      </label>

      <section className="flex flex-col gap-1.5 text-sm">
        <p className="text-muted-foreground">{content.summary}</p>
        {module.dependsOn.length > 0 ? (
          <p className="text-xs text-muted-foreground">
            Uses {module.dependsOn.map((id) => MODULES[id].name).join(", ")}
          </p>
        ) : null}
        {module.requiresIntegrations.length > 0 ? (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Plugs className="size-3.5 shrink-0" />
            Needs{" "}
            {module.requiresIntegrations
              .map((id) => INTEGRATION_LABELS[id])
              .join(", ")}{" "}
            (the company connects it in Settings)
          </p>
        ) : null}
      </section>

      {on && module.options?.length ? (
        <section className="flex flex-col gap-2">
          <h4 className="text-sm font-semibold">Options</h4>
          {module.options.map((option) => (
            <label
              key={option.key}
              className="flex items-center justify-between gap-3 rounded-md bg-muted/50 px-3 py-2"
            >
              <span className="min-w-0">
                <span className="block text-sm font-medium">
                  {option.label}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {option.description}
                </span>
              </span>
              <Switch
                checked={row?.options?.[option.key] === true}
                disabled={busy}
                onCheckedChange={(checked) =>
                  onChange([
                    {
                      moduleId: module.id,
                      enabled: true,
                      options: {
                        ...(row?.options ?? {}),
                        [option.key]: checked,
                      },
                    },
                  ])
                }
              />
            </label>
          ))}
        </section>
      ) : null}

      {module.features?.length ? (
        <section className="flex flex-col gap-2">
          <h4 className="text-sm font-semibold">Features</h4>
          {on ? (
            <ModuleFeatureSwitches
              module={module}
              rows={rows}
              busy={busy}
              onChange={onChange}
              variant="compact"
            />
          ) : (
            <ul className="flex flex-col gap-1.5">
              {module.features.map((feature) => (
                <li
                  key={feature.key}
                  className="rounded-md bg-muted/50 px-3 py-2 text-sm"
                >
                  <span className="font-medium">{feature.label}</span>
                  <span className="block text-xs text-muted-foreground">
                    {feature.description}
                  </span>
                </li>
              ))}
              <li className="text-xs text-muted-foreground">
                Turn the app on to choose its features.
              </li>
            </ul>
          )}
        </section>
      ) : null}

      {on && module.automations?.length ? (
        <section className="flex flex-col gap-2">
          <h4 className="text-sm font-semibold">Automations</h4>
          <ModuleAutomationSwitches
            module={module}
            rows={rows}
            busy={busy}
            onChange={onChange}
            variant="compact"
          />
        </section>
      ) : null}

      {content.benefits.length ? (
        <section className="flex flex-col gap-2">
          <h4 className="text-sm font-semibold">What it does</h4>
          <ul className="flex flex-col gap-1.5 text-sm">
            {content.benefits.map((benefit) => (
              <li key={benefit.title}>
                <span className="font-medium">{benefit.title}</span>
                <span className="block text-xs text-muted-foreground">
                  {benefit.description}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
};
